import React, { createContext, useContext, useState } from 'react';
import { supabase, companyDb } from '../supabaseClient.ts';

type TutorSession = {
  id: string;
  studentUserId: string;
  returnUrl?: string;
};

type TutorSessionCtx = {
  isTutorSession: boolean;
  tutorSession: TutorSession | null;
  activateSession: (sessionId: string, returnUrl?: string) => Promise<void>;
  clearSession: () => void;
  endTutorSession: () => Promise<void>;
};

const STORAGE_KEY = 'tutorSessionStateV1';

const TutorSessionContext = createContext<TutorSessionCtx | null>(null);

const readFromStorage = (): TutorSession | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.id || !parsed?.studentUserId) return null;
    return parsed as TutorSession;
  } catch {
    return null;
  }
};

const writeToStorage = (value: TutorSession | null) => {
  try {
    if (!value) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore
  }
};

export const TutorSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tutorSession, setTutorSession] = useState<TutorSession | null>(() => readFromStorage());

  const clearSession = () => {
    setTutorSession(null);
    writeToStorage(null);
  };

  const activateSession = async (sessionId: string, returnUrl?: string) => {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) throw new Error('Not authenticated.');

    const { data, error } = await companyDb
      .from('tutor_sessions')
      .select('id, tutor_user_id, student_user_id, status, ended_at')
      .eq('id', sessionId)
      .single();

    if (error || !data) {
      throw new Error(error?.message ?? 'Tutor session not found.');
    }
    if (String((data as any).tutor_user_id) !== String(auth.user.id)) {
      throw new Error('This tutor session does not belong to you.');
    }
    if ((data as any).ended_at || (data as any).status !== 'active') {
      throw new Error('Tutor session has already ended.');
    }

    const next: TutorSession = {
      id: String((data as any).id),
      studentUserId: String((data as any).student_user_id),
      returnUrl: returnUrl || undefined,
    };
    setTutorSession(next);
    writeToStorage(next);
  };

  const endTutorSession = async () => {
    if (!tutorSession?.id) return;
    const { error } = await supabase.functions.invoke('end_tutor_session', {
      body: { sessionId: tutorSession.id },
    });
    if (error) throw error;
    clearSession();
  };

  const value: TutorSessionCtx = {
    isTutorSession: Boolean(tutorSession),
    tutorSession,
    activateSession,
    clearSession,
    endTutorSession,
  };

  return (
    <TutorSessionContext.Provider value={value}>
      {children}
    </TutorSessionContext.Provider>
  );
};

export const useTutorSession = () => {
  const ctx = useContext(TutorSessionContext);
  if (!ctx) {
    throw new Error('useTutorSession must be used inside TutorSessionProvider');
  }
  return ctx;
};
